// Central fetch wrapper for the super-admin portal.
//
// Every request goes through `apiFetch`, which:
//   • prefixes the API base (VITE_API_URL, default "/api/v1")
//   • attaches the bearer token
//   • serializes JSON bodies and query params
//   • normalizes EVERY failure (HTTP error, bad JSON, network) into ApiError
//     with a stable `code`, so pages never parse error bodies themselves
//   • reports 401s to the auth layer (onUnauthorized), which logs out and
//     sends the user to /login?next=…
//
// No tenant header (X-League-Id) is ever sent: platform routes are cross-tenant.

import type { ApiErrorCode } from './types'

export const API_BASE: string = import.meta.env.VITE_API_URL || '/api/v1'

export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly field?: string
  readonly details?: Record<string, unknown>
  /** A finer reason beside a coarse code, e.g. `invalid_token` on the reset route's 400 `bad_request`. */
  readonly reason?: string

  constructor(opts: {
    status: number
    code: ApiErrorCode
    message: string
    field?: string
    details?: Record<string, unknown>
    reason?: string
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.status = opts.status
    this.code = opts.code
    this.field = opts.field
    this.details = opts.details
    this.reason = opts.reason
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError
}

/** A user-facing sentence for any thrown value. */
export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error && err.message) return err.message
  return fallback
}

// ─── Configuration (set by the auth layer at startup) ───────────────────────

interface ClientConfig {
  getToken: () => string | null
  onUnauthorized: (err: ApiError) => void
}

const config: ClientConfig = {
  getToken: () => null,
  onUnauthorized: () => {},
}

export function configureClient(partial: Partial<ClientConfig>) {
  Object.assign(config, partial)
}

// ─── "The session ended" (a reported 401) ───────────────────────────────────
// Set when a 401 is reported to the auth layer, cleared when a session is
// written (sign-in). While it is set, useQuery keeps a 401 query loading,
// ErrorState renders nothing for a 401 and toast.error shows nothing, so the
// page never shows an error on its way to /login?next=… (RequireAuth).

let sessionEnded = false

export function isSessionEnded(): boolean {
  return sessionEnded
}

export function resetSessionEnded(): void {
  sessionEnded = false
}

// ─── Request ────────────────────────────────────────────────────────────────

export type QueryValue = string | number | boolean | null | undefined
export type Query = Record<string, QueryValue>

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  query?: Query
  body?: unknown
  signal?: AbortSignal
  /** Send the bearer token (default true). Login sets false. */
  auth?: boolean
  /**
   * Report a 401 to the auth layer (default true). Login sets false, since
   * a 401 there means "wrong password", not "session expired".
   */
  handleUnauthorized?: boolean
  /**
   * Send THIS bearer instead of the stored session's (the invite page claims
   * an invite with the token a sign-in just issued, before anything is
   * stored). A 401 is then never reported to the auth layer.
   */
  bearer?: string
}

export function buildQuery(query?: Query): string {
  if (!query) return ''
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue
    qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, signal, auth = true, bearer } = opts
  const handleUnauthorized = bearer ? false : opts.handleUnauthorized ?? true
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`
  } else if (auth) {
    const token = config.getToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(query)}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: "Couldn't reach the server. Check your connection and try again.",
    })
  }

  if (!res.ok) {
    const error = await toApiError(res)
    if (res.status === 401 && handleUnauthorized) {
      sessionEnded = true
      config.onUnauthorized(error)
    }
    throw error
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined as T
  const text = await res.text()
  if (!text) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError({ status: res.status, code: 'server_error', message: 'The server sent a response the portal could not read.' })
  }
}

// ─── Error normalization ────────────────────────────────────────────────────

const STATUS_CODES: Record<number, ApiErrorCode> = {
  400: 'bad_request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'validation_failed',
  429: 'rate_limited',
  502: 'cloudflare_error',
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was not valid.',
  401: 'Your session has expired. Sign in again.',
  403: "You don't have permission to do that.",
  404: 'Not found.',
  409: 'That conflicts with existing data.',
  429: 'Too many requests. Wait a minute and try again.',
}

/**
 * Accepts every error body shape the backend sends:
 *   new:    { error: { code, message, field?, details? } }
 *   compat: { error: "Bad Request", code, message, field?, reason? }   (sendCompatError)
 *   Fastify: { error: "Bad Request", message: "…" }   (its own 400s and 404s)
 *   bare:   { message: "…" } or non-JSON
 */
export async function toApiError(res: Response): Promise<ApiError> {
  let body: any = null
  try {
    const text = await res.text()
    body = text ? JSON.parse(text) : null
  } catch {
    body = null
  }

  const nested = body && typeof body.error === 'object' && body.error !== null ? body.error : null
  const message: string =
    (nested && typeof nested.message === 'string' && nested.message) ||
    (body && typeof body.message === 'string' && body.message) ||
    STATUS_MESSAGES[res.status] ||
    (res.status >= 500 ? 'The server hit an error. Try again, or check the backend logs.' : res.statusText || 'Request failed')

  const code: ApiErrorCode =
    (nested && typeof nested.code === 'string' && (nested.code as ApiErrorCode)) ||
    (body && typeof body.code === 'string' && /^[a-z_]+$/.test(body.code) && (body.code as ApiErrorCode)) ||
    STATUS_CODES[res.status] ||
    (res.status >= 500 ? 'server_error' : 'bad_request')

  const reason: unknown = nested?.reason ?? body?.reason
  return new ApiError({
    status: res.status,
    code,
    message,
    field: nested?.field ?? body?.field,
    details: nested?.details,
    reason: typeof reason === 'string' ? reason : undefined,
  })
}
